import * as React from 'react'
import { ReactElement, useContext, useMemo } from 'react'
import { Button, Card, makeStyles, shorthands, Title3, tokens, Tooltip, TreeItemPersonaLayout } from '@fluentui/react-components'
import IdentityContext, { Identity, IdentityContextProps } from '../../context/IdentityContext'
import { Tree, TreeItem } from '@fluentui/react-components/unstable'
import { AddFilled, AddRegular, bundleIcon, SignOutFilled, SignOutRegular } from '@fluentui/react-icons'
import { AppAction } from '../../config/appConfig'
import { useTranslation } from 'react-i18next'
import PersonAvatar from '../PersonAvatar'
import { DateTime } from 'luxon'

const useStyles = makeStyles({
    container: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    item: {
        paddingLeft: tokens.spacingHorizontalS,
    },
})

const Add = bundleIcon(AddFilled, AddRegular)
const SignOut = bundleIcon(SignOutFilled, SignOutRegular)

export default function SelectAccount(): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { accounts } = useContext<IdentityContextProps>(IdentityContext)

    const activeAccounts = useMemo(
        () =>
            accounts.filter(account => {
                let exp: DateTime<true | false> = DateTime.now().toUTC()
                const now = DateTime.now().toUTC()
                if (account?.idTokenClaims?.exp) {
                    exp = DateTime.fromMillis(account?.idTokenClaims?.exp * 1000, { zone: 'UTC' })
                }
                return exp.toMillis() - now.toMillis() - 10 * 1000 > 0
            }),
        [accounts],
    )

    return (
        <div className={styles.container}>
            <Title3>{translate('authentication.account.select.title')} </Title3>
            <Card>
                <Tree aria-label="Accounts">
                    {activeAccounts?.map(account => (
                        <TreeItem
                            key={account.username.toLowerCase()}
                            itemType="leaf"
                            onClick={() => Identity.current?.selectAccount(account.username)}
                        >
                            <TreeItemPersonaLayout
                                media={<PersonAvatar />}
                                description={account.username.toLowerCase()}
                                className={styles.item}
                                aside={
                                    <Tooltip content={translate('authentication.account.select.logoff')} relationship="label" withArrow>
                                        <Button
                                            appearance="subtle"
                                            icon={<SignOut />}
                                            onClick={() => Identity.current?.logout(AppAction.LogOut, account.username)}
                                        />
                                    </Tooltip>
                                }
                            >
                                {account.name}
                            </TreeItemPersonaLayout>
                        </TreeItem>
                    ))}

                    <TreeItem itemType="leaf">
                        <TreeItemPersonaLayout
                            media={<PersonAvatar icon={<Add />} />}
                            className={styles.item}
                            onClick={() => Identity.current?.login(true)}
                        >
                            {accounts?.length > 0
                                ? translate('authentication.account.select.other')
                                : translate('authentication.account.select.login')}
                        </TreeItemPersonaLayout>
                    </TreeItem>
                </Tree>
            </Card>
        </div>
    )
}
